// Tipos para o módulo de Business Intelligence

export enum FinancialEventType {
  EMISSAO_2VIA = 'EMISSAO_2VIA',
  NEGOCIACAO = 'NEGOCIACAO',
  ALERTA_SUSPENSAO = 'ALERTA_SUSPENSAO',
}

export enum FinancialEventStatus {
  PENDENTE = 'PENDENTE',
  PAGO = 'PAGO',
  CANCELADO = 'CANCELADO',
  VENCIDO = 'VENCIDO',
}

export enum AIPromptType {
  ORCHESTRATOR = 'ORCHESTRATOR',
  AUDITOR = 'AUDITOR',
}

export enum InteractionStatus {
  OPEN = 'open',
  PENDING = 'pending',
  RESOLVED = 'resolved',
  CLOSED = 'closed',
  CANCELLED = 'cancelled',
}

export enum ContactType {
  ATIVO = 'ativo',
  RECEPTIVO = 'receptivo',
}

// ROI Dashboard
export interface ROIDashboardData {
  totalRecovered: number;
  totalGenerated: number;
  conversionRate: number;
  averagePaymentTimePix: number | null; // em horas
  byEventType: {
    event_type: FinancialEventType;
    totalRecovered: number;
    totalGenerated: number;
    conversionRate: number;
    count: number;
  }[];
  period: {
    startDate: string;
    endDate: string;
  };
}

// Interaction Metrics
export interface InteractionMetricsData {
  totalInteractions: number;
  averageSentiment: number | null;
  slaViolations: number;
  slaViolationRate: number;
  transfersCount: number;
  transferRate: number;
  averageResponseTime: number | null; // em minutos
  averageResolutionTime: number | null; // em horas
  byDepartment: {
    department: string;
    count: number;
    averageSentiment: number | null;
  }[];
  byAgent: {
    agent_id: string;
    agent_name: string | null;
    department: string;
    count: number;
    averageSentiment: number | null;
    slaViolations: number;
    transfersCount: number;
  }[];
  byStatus: {
    status: string;
    count: number;
    percentage: number;
  }[];
  sentimentOverTime: {
    date: string;
    averageSentiment: number | null;
    count: number;
  }[];
}

// Organizational Structure
export interface OrganizationalStructure {
  id: number;
  client_id: number;
  agent_id: number;
  agent_name: string;
  department_name: string;
  department_id: number;
  contact_phone?: string | null;
  created_at: string;
  updated_at: string;
}

// Financial Event
export interface FinancialEvent {
  id: string;
  client_id: number;
  interaction_id?: string | null;
  invoice_id: string;
  event_type: FinancialEventType;
  amount_generated: number;
  amount_paid: number;
  due_date?: string | null;
  payment_date?: string | null;
  payment_method?: string | null;
  status: FinancialEventStatus;
  created_at: string;
  updated_at: string;
  interaction?: {
    protocol: string;
    customer_name: string;
    customer_phone: string;
    start_time: string;
  };
}

// Financial Events Response
export interface FinancialEventsResponse {
  data: FinancialEvent[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// AI Prompt
export interface AIPrompt {
  id: number;
  client_id?: number | null;
  prompt_type: AIPromptType;
  name: string;
  description?: string | null;
  system_prompt: string;
  user_prompt_template: string;
  output_schema?: Record<string, any> | null;
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

// Update Prompt Request
export interface UpdatePromptRequest {
  name?: string;
  description?: string;
  system_prompt?: string;
  user_prompt_template?: string;
  output_schema?: Record<string, any>;
  is_active?: boolean;
}

// Interaction Lifecycle
export interface InteractionLifecycle {
  protocol: string; // UUID
  client_id: number;
  conversation_id?: number | null;
  customer_id: string;
  customer_name: string;
  customer_phone: string;
  start_time: string;
  end_time?: string | null;
  first_response_time?: string | null;
  initial_department?: string | null;
  final_department?: string | null;
  initial_agent_id?: string | null;
  final_agent_id?: string | null;
  was_transferred: boolean;
  sla_violation: boolean;
  status: InteractionStatus;
  conversation_summary?: string | null;
  sentiment_score?: number | null; // -100 a 100
  tags?: string[];
  connection?: string | null;
  type_contact?: ContactType | null;
  created_at: string;
  updated_at: string;
}

// Interaction Message
export enum SenderType {
  AGENT = 'agent',
  USER = 'user',
}

export interface InteractionMessage {
  id: number;
  interaction_protocol: string; // UUID
  client_id: number;
  content: string;
  sender_type: SenderType;
  sender_name: string;
  sent_at: string;
}

// Interactive Menu (WhatsApp List Messages)
export interface InteractiveMenuRow {
  id: string; // Row ID (máx 200 chars) - usado no webhook
  title: string; // Máx 24 chars
  description?: string; // Máx 72 chars, opcional
}

export interface InteractiveMenuSection {
  id: string; // ID interno para drag and drop
  title: string; // Section title (máx 24 chars)
  rows: InteractiveMenuRow[];
}

export interface InteractiveMenu {
  id: number;
  client_id: number;
  name: string;
  description?: string | null;
  button_text: string; // Máx 20 chars
  header_text?: string | null; // Máx 60 chars, opcional
  body_text: string; // Máx 4096 chars
  footer_text?: string | null; // Máx 60 chars, opcional
  sections: InteractiveMenuSection[]; // JSONB no banco
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateInteractiveMenuRequest {
  client_id: number;
  name: string;
  description?: string;
  button_text: string;
  header_text?: string;
  body_text: string;
  footer_text?: string;
  sections: InteractiveMenuSection[];
  is_active?: boolean;
}

export interface UpdateInteractiveMenuRequest {
  name?: string;
  description?: string;
  button_text?: string;
  header_text?: string;
  body_text?: string;
  footer_text?: string;
  sections?: InteractiveMenuSection[];
  is_active?: boolean;
}

export interface SendInteractiveMenuRequest {
  menu_id: number;
  phone_number: string;
  connection_id: number;
}

